import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
// AppContext no longer needed — quote submission uses Netlify function
import {
  Leaf, User, Mail, Phone, MapPin, Home, Bath, Users, Dog,
  Plus, Minus, Camera, Upload, X, CheckCircle, AlertCircle,
  Sparkles, Calendar, ChevronRight, ChevronDown, Info, Loader
} from 'lucide-react';
import './QuoteRequest.css';

// Cleaning products we use
const CLEANING_PRODUCTS = [
  { name: 'Simple Green', description: 'All-purpose cleaner' },
  { name: 'Alcohol Glass Cleaner', description: 'Made in-house' },
  { name: 'Odoban', description: 'pH neutral floor cleaner' },
  { name: 'Beeswax (No Film)', description: 'Glass, mirrors, wood furniture, stainless, counters, dusting' }
];

// Home condition options
const HOME_CONDITIONS = [
  { 
    value: 'super_clean', 
    label: 'Super Clean Freaks', 
    description: 'Immaculate home, minimal cleaning needed',
    emoji: '✨'
  },
  { 
    value: 'second_home', 
    label: 'Clean (Second Home)', 
    description: 'Rarely used, stays tidy',
    emoji: '🏠'
  },
  { 
    value: 'lived_in', 
    label: 'Clean but Lived In', 
    description: 'Normal family home, regular use',
    emoji: '👨‍👩‍👧'
  },
  { 
    value: 'busy_home', 
    label: "There's a Lot of Us", 
    description: 'Busy household, lots of activity',
    emoji: '👨‍👩‍👧‍👦'
  },
  { 
    value: 'overwhelmed', 
    label: "I'm Overwhelmed", 
    description: 'Needs extra attention and care',
    emoji: '🆘'
  }
];

// Frequency options
const FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly', description: 'Best value per clean' },
  { value: 'biweekly', label: 'Every 2 Weeks', description: 'Most popular' },
  { value: 'monthly', label: 'Monthly', description: 'Great for maintenance' },
  { value: 'one_time', label: 'One-Time Only', description: 'No commitment' }
];

// Oven conditions
const OVEN_CONDITIONS = [
  { value: 'needs_love', label: 'Needs Love', description: 'Light grease, regular use', price: '$35', image: '/images/conditions/oven-needs-love.png' },
  { value: 'extra_love', label: 'Needs Extra Love', description: 'Moderate buildup', price: '$50', image: '/images/conditions/oven-extra-love.png' },
  { value: 'elbow_grease', label: 'Serious Elbow Grease', description: 'Heavy buildup/neglected', price: '$100', image: '/images/conditions/oven-elbow-grease.png' }
];

// Fridge conditions  
const FRIDGE_CONDITIONS = [
  { value: 'quick_wipe', label: 'Quick Wipe', description: 'Empty or nearly empty', price: '$25', image: '/images/conditions/fridge-quick-wipe.png' },
  { value: 'semi_full', label: 'Semi Full/Deep Clean', description: 'Partially full, needs attention', price: '$50', image: '/images/conditions/fridge-semi-full.png' },
  { value: 'full', label: 'Full/Extra Love', description: 'Full fridge, thorough clean', price: '$100', image: '/images/conditions/fridge-full.png' }
];

function QuoteRequest() {
  const navigate = useNavigate();
  // Submission handled entirely via Netlify function — no auth required
  const fileInputRef = useRef(null);
  
  // Form state
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serviceType, setServiceType] = useState(''); // cleaning, hoarding, junk_removal, custom
  const [expandedSections, setExpandedSections] = useState({
    oven: false,
    fridge: false,
    windows: false,
    other: false
  });
  
  // Client info
  const [clientInfo, setClientInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    marketingOptIn: false
  });
  
  // Property info
  const [propertyInfo, setPropertyInfo] = useState({
    street: '',
    city: '',
    state: 'VA',
    zip: '',
    bedrooms: 3,
    fullBaths: 2,
    halfBaths: 0,
    condition: '',
    people: 2,
    sheddingPets: 0,
    petDetails: '',
    glassShowerDoors: 0
  });
  
  // Service preferences
  const [servicePrefs, setServicePrefs] = useState({
    frequencies: ['biweekly'], // Allow multiple frequency selection
    preferredDay: '',
    preferredTime: '',
    specialRequests: '',
    // For custom/hoarding/junk removal quotes
    customDescription: '',
    // Cleaning products
    approveProducts: null,
    productConcerns: ''
  });

  // Commercial info
  const [commercialInfo, setCommercialInfo] = useState({
    businessName: '',
    buildingType: '',
    sqft: '',
    floors: '1',
    frequency: 'weekly',
    preferredSchedule: '',
    additionalDetails: ''
  });
  
  // Add-ons
  const [addons, setAddons] = useState({
    // Oven
    ovenClean: false,
    ovenCondition: '',
    ovenIsBlueCeramic: false,
    ovenPhotoUrl: null,
    // Fridge
    fridgeClean: false,
    fridgeCondition: '',
    fridgePhotoUrl: null,
    // Windows
    interiorWindows: 0,
    windowTracks: 0,
    blinds: 0,
    // Other
    organizing: false,
    organizingHours: 1,
    groutCleaning: false,
    extraBuildup: false
  });
  
  // Photos
  const [photos, setPhotos] = useState([]);
  const [photoError, setPhotoError] = useState('');
  
  // Terms acceptance
  const [termsAcceptance, setTermsAcceptance] = useState({
    termsAgree: false,
    ageConfirm: false,
    paymentAgree: false,
    photoAgree: false,
    arbitrationAgree: false,
    liabilityAgree: false
  });
  
  // Form validation
  const [errors, setErrors] = useState({});
  
  // Toggle section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  // Handle photo upload
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    
    if (photos.length + files.length > 15) {
      setPhotoError('Maximum 15 photos allowed');
      return;
    }
    
    files.forEach(file => {
      if (!file.type.startsWith('image/')) {
        setPhotoError('Please upload only image files');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setPhotos(prev => [...prev, {
          id: Date.now() + Math.random(),
          file: file,
          preview: event.target.result,
          label: ''
        }]);
      };
      reader.readAsDataURL(file);
    });
    
    setPhotoError('');
  };
  
  // Remove photo
  const removePhoto = (id) => {
    setPhotos(prev => prev.filter(p => p.id !== id));
  };
  
  // Update photo label
  const updatePhotoLabel = (id, label) => {
    setPhotos(prev => prev.map(p => p.id === id ? { ...p, label } : p));
  };
  
  // Number input component
  const NumberInput = ({ label, value, onChange, min = 0, max = 20, helpText }) => (
    <div className="number-input-group">
      <label>{label}</label>
      <div className="number-controls">
        <button 
          type="button" 
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
        >
          <Minus size={16} />
        </button>
        <span className="number-value">{value}</span>
        <button 
          type="button"
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={value >= max}
        >
          <Plus size={16} />
        </button>
      </div>
      {helpText && <span className="help-text">{helpText}</span>}
    </div>
  );
  
  // Validate step
  const validateStep = (stepNum) => {
    const newErrors = {};
    
    if (stepNum === 1) {
      if (!clientInfo.firstName.trim()) newErrors.firstName = 'Required';
      if (!clientInfo.lastName.trim()) newErrors.lastName = 'Required';
      if (!clientInfo.email.trim()) newErrors.email = 'Required';
      else if (!/\S+@\S+\.\S+/.test(clientInfo.email)) newErrors.email = 'Invalid email';
      if (!clientInfo.phone.trim()) newErrors.phone = 'Required';
      if (!clientInfo.marketingOptIn) newErrors.marketingOptIn = 'Please agree to receive emails';
    }
    
    if (stepNum === 2) {
      if (!propertyInfo.street.trim()) newErrors.street = 'Required';
      if (!propertyInfo.city.trim()) newErrors.city = 'Required';
      if (!propertyInfo.zip.trim()) newErrors.zip = 'Required';
      if (!propertyInfo.condition) newErrors.condition = 'Please select a condition';
    }
    
    if (stepNum === 3) {
      if (!servicePrefs.frequencies || servicePrefs.frequencies.length === 0) newErrors.frequencies = 'Please select at least one frequency';
      if (servicePrefs.approveProducts === null) newErrors.approveProducts = 'Please confirm product approval';
    }
    
    // Step 4 (photos) has no required fields
    
    if (stepNum === 5) {
      if (!termsAcceptance.termsAgree) newErrors.termsAgree = 'Required';
      if (!termsAcceptance.ageConfirm) newErrors.ageConfirm = 'Required';
      if (!termsAcceptance.paymentAgree) newErrors.paymentAgree = 'Required';
      if (!termsAcceptance.photoAgree) newErrors.photoAgree = 'Required';
      if (!termsAcceptance.arbitrationAgree) newErrors.arbitrationAgree = 'Required';
      if (!termsAcceptance.liabilityAgree) newErrors.liabilityAgree = 'Required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Go to next step
  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
      window.scrollTo(0, 0);
    }
  };
  
  // Go to previous step
  const prevStep = () => {
    setStep(step - 1);
    window.scrollTo(0, 0);
  };

  // Submit data via Netlify function (works for unauthenticated public users)
  const submitViaFunction = async (clientData, propertyData, estimateData, svcType) => {
    const response = await fetch('/.netlify/functions/submit-quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client: clientData,
        property: propertyData,
        estimate: estimateData,
        serviceType: svcType
      })
    });
    
    if (!response.ok) {
      const errBody = await response.text();
      console.error('Submit quote function error:', errBody);
      throw new Error('Failed to submit quote request');
    }
    
    return await response.json();
  };

  // Submit simple quote (hoarding, junk removal, custom)
  const handleSimpleQuoteSubmit = async () => {
    // Basic validation
    const newErrors = {};
    if (!clientInfo.firstName?.trim()) newErrors.firstName = 'Required';
    if (!clientInfo.lastName?.trim()) newErrors.lastName = 'Required';
    if (!clientInfo.email?.trim()) newErrors.email = 'Required';
    if (!clientInfo.phone?.trim()) newErrors.phone = 'Required';
    if (!servicePrefs.customDescription?.trim()) newErrors.customDescription = 'Please describe your project';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const serviceLabels = {
        hoarding: 'Hoarding Help',
        junk_removal: 'Junk Removal',
        custom: 'Custom Quote'
      };
      
      await submitViaFunction(
        {
          firstName: clientInfo.firstName,
          lastName: clientInfo.lastName,
          email: clientInfo.email,
          phone: clientInfo.phone,
          clientType: 'residential',
          notes: servicePrefs.customDescription
        },
        propertyInfo.street ? {
          street: propertyInfo.street,
          city: propertyInfo.city || 'Charlottesville',
          state: propertyInfo.state || 'VA',
          zip: propertyInfo.zip || ''
        } : null,
        {
          serviceType: serviceType,
          title: `${serviceLabels[serviceType]} Request`,
          notes: servicePrefs.customDescription,
          customDescription: servicePrefs.customDescription,
          photoCount: photos.length
        },
        serviceType
      );
      
      // Send notification email to admin
      try {
        await fetch('/.netlify/functions/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'new_quote_request',
            data: {
              clientName: `${clientInfo.firstName} ${clientInfo.lastName}`.trim(),
              clientEmail: clientInfo.email,
              clientPhone: clientInfo.phone,
              serviceType: serviceLabels[serviceType] || serviceType,
              description: servicePrefs.customDescription,
              address: propertyInfo.street || 'Not provided',
              hasPhotos: photos.length > 0,
              photoCount: photos.length
            }
          })
        });
      } catch (emailError) {
        console.error('Admin email error:', emailError);
      }
      
      // Send confirmation email to client
      try {
        await fetch('/.netlify/functions/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'quote_request_confirmation',
            data: {
              clientName: `${clientInfo.firstName} ${clientInfo.lastName}`.trim(),
              clientEmail: clientInfo.email,
              serviceType: serviceLabels[serviceType] || serviceType,
              description: servicePrefs.customDescription
            }
          })
        });
      } catch (emailError) {
        console.error('Client email error:', emailError);
      }
      
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting quote request:', error);
      alert('There was an error submitting your request. Please try again or call us at (434) 207-2246');
    }
    
    setIsSubmitting(false);
  };

  // Submit commercial quote
  const handleCommercialQuoteSubmit = async () => {
    // Validation
    const newErrors = {};
    if (!clientInfo.firstName?.trim()) newErrors.firstName = 'Required';
    if (!clientInfo.lastName?.trim()) newErrors.lastName = 'Required';
    if (!clientInfo.email?.trim()) newErrors.email = 'Required';
    if (!clientInfo.phone?.trim()) newErrors.phone = 'Required';
    if (!commercialInfo.businessName?.trim()) newErrors.businessName = 'Required';
    if (!propertyInfo.street?.trim()) newErrors.street = 'Required';
    if (!commercialInfo.buildingType) newErrors.buildingType = 'Required';
    if (!commercialInfo.sqft?.trim()) newErrors.sqft = 'Required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const frequencyLabels = {
        daily: 'Daily',
        '3x_week': '3x Per Week',
        '2x_week': '2x Per Week',
        weekly: 'Weekly',
        biweekly: 'Every 2 Weeks',
        monthly: 'Monthly',
        one_time: 'One-Time Deep Clean'
      };

      const buildingLabels = {
        office: 'Office Building',
        retail: 'Retail Store',
        medical: 'Medical/Dental Office',
        restaurant: 'Restaurant/Food Service',
        gym: 'Gym/Fitness Center',
        church: 'Church/Religious Facility',
        school: 'School/Educational',
        warehouse: 'Warehouse/Industrial',
        salon: 'Salon/Spa',
        airbnb: 'Short-Term Rental/Airbnb',
        other: 'Other'
      };

      await submitViaFunction(
        {
          firstName: clientInfo.firstName,
          lastName: clientInfo.lastName,
          email: clientInfo.email,
          phone: clientInfo.phone,
          clientType: 'commercial',
          businessName: commercialInfo.businessName,
          notes: `Building Type: ${buildingLabels[commercialInfo.buildingType] || commercialInfo.buildingType}\nSq Ft: ${commercialInfo.sqft}\nFloors: ${commercialInfo.floors}\nFrequency: ${frequencyLabels[commercialInfo.frequency]}\nPreferred Schedule: ${commercialInfo.preferredSchedule || 'Flexible'}\n\nAdditional Details:\n${commercialInfo.additionalDetails || 'None'}`
        },
        {
          street: propertyInfo.street,
          city: propertyInfo.city || 'Charlottesville',
          state: propertyInfo.state || 'VA',
          zip: propertyInfo.zip || '',
          sqft: commercialInfo.sqft,
          propertyType: 'commercial',
          buildingType: commercialInfo.buildingType
        },
        {
          serviceType: 'commercial',
          title: `Commercial Cleaning - ${commercialInfo.businessName}`,
          businessName: commercialInfo.businessName,
          buildingType: buildingLabels[commercialInfo.buildingType] || commercialInfo.buildingType,
          sqft: commercialInfo.sqft,
          floors: commercialInfo.floors,
          frequency: frequencyLabels[commercialInfo.frequency],
          preferredSchedule: commercialInfo.preferredSchedule || 'Flexible',
          notes: commercialInfo.additionalDetails,
          photoCount: photos.length
        },
        'commercial'
      );
      
      // Send notification email to admin
      try {
        await fetch('/.netlify/functions/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'new_quote_request',
            data: {
              clientName: `${clientInfo.firstName} ${clientInfo.lastName}`.trim(),
              clientEmail: clientInfo.email,
              clientPhone: clientInfo.phone,
              serviceType: 'Commercial Cleaning',
              businessName: commercialInfo.businessName,
              buildingType: buildingLabels[commercialInfo.buildingType] || commercialInfo.buildingType,
              sqft: commercialInfo.sqft,
              floors: commercialInfo.floors,
              frequency: frequencyLabels[commercialInfo.frequency],
              preferredSchedule: commercialInfo.preferredSchedule || 'Flexible',
              description: commercialInfo.additionalDetails,
              address: propertyInfo.street,
              hasPhotos: photos.length > 0,
              photoCount: photos.length
            }
          })
        });
      } catch (emailError) {
        console.error('Admin email error:', emailError);
      }
      
      // Send confirmation email to client
      try {
        await fetch('/.netlify/functions/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'quote_request_confirmation',
            data: {
              clientName: `${clientInfo.firstName} ${clientInfo.lastName}`.trim(),
              clientEmail: clientInfo.email,
              serviceType: 'Commercial Cleaning',
              description: `${commercialInfo.businessName} - ${buildingLabels[commercialInfo.buildingType] || commercialInfo.buildingType}, ${commercialInfo.sqft} sq ft, ${frequencyLabels[commercialInfo.frequency]}`
            }
          })
        });
      } catch (emailError) {
        console.error('Client email error:', emailError);
      }
      
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting commercial quote request:', error);
      alert('There was an error submitting your request. Please try again or call us at (434) 207-2246');
    }
    
    setIsSubmitting(false);
  };
  
  // Submit the form
  const handleSubmit = async () => {
    if (!validateStep(5)) return;
    
    setIsSubmitting(true);
    
    try {
      // Build selected addons list
      const selectedAddons = [];
      
      if (addons.ovenClean && addons.ovenCondition) {
        selectedAddons.push({
          id: `oven_${addons.ovenCondition}`,
          label: `Inside Oven - ${OVEN_CONDITIONS.find(o => o.value === addons.ovenCondition)?.label}`,
          isBlueCeramic: addons.ovenIsBlueCeramic,
          photoUrl: addons.ovenPhotoUrl
        });
      }
      
      if (addons.fridgeClean && addons.fridgeCondition) {
        selectedAddons.push({
          id: `fridge_${addons.fridgeCondition}`,
          label: `Inside Fridge - ${FRIDGE_CONDITIONS.find(f => f.value === addons.fridgeCondition)?.label}`,
          photoUrl: addons.fridgePhotoUrl
        });
      }
      
      if (addons.interiorWindows > 0) {
        selectedAddons.push({ id: 'interior_windows', quantity: addons.interiorWindows });
      }
      if (addons.windowTracks > 0) {
        selectedAddons.push({ id: 'window_tracks', quantity: addons.windowTracks });
      }
      if (addons.blinds > 0) {
        selectedAddons.push({ id: 'blinds', quantity: addons.blinds });
      }
      if (addons.organizing) {
        selectedAddons.push({ id: 'organizing', hours: addons.organizingHours });
      }
      if (addons.groutCleaning) {
        selectedAddons.push({ id: 'grout_cleaning', requiresReview: true });
      }
      if (addons.extraBuildup) {
        selectedAddons.push({ id: 'extra_buildup', requiresReview: true });
      }
      
      const fullAddress = `${propertyInfo.street}, ${propertyInfo.city}, ${propertyInfo.state} ${propertyInfo.zip}`;
      
      await submitViaFunction(
        {
          firstName: clientInfo.firstName,
          lastName: clientInfo.lastName,
          email: clientInfo.email,
          phone: clientInfo.phone,
          clientType: 'residential',
          notes: ''
        },
        {
          street: propertyInfo.street,
          city: propertyInfo.city,
          state: propertyInfo.state,
          zip: propertyInfo.zip,
          bedrooms: propertyInfo.bedrooms,
          bathrooms: propertyInfo.fullBaths + (propertyInfo.halfBaths * 0.5),
          condition: propertyInfo.condition,
          pets: propertyInfo.sheddingPets > 0 ? `${propertyInfo.sheddingPets} shedding pet(s)` : 'None',
          petDetails: propertyInfo.petDetails
        },
        {
          serviceType: 'residential',
          title: 'Residential Quote Request',
          calculatorInputs: {
            sqft: null,
            condition: propertyInfo.condition,
            bedrooms: propertyInfo.bedrooms,
            fullBaths: propertyInfo.fullBaths,
            halfBaths: propertyInfo.halfBaths,
            people: propertyInfo.people,
            sheddingPets: propertyInfo.sheddingPets,
            glassShowerDoors: propertyInfo.glassShowerDoors,
            frequency: servicePrefs.frequencies?.[0] || 'biweekly'
          },
          frequencies: servicePrefs.frequencies || ['biweekly'],
          selectedAddons: selectedAddons,
          accessInstructions: servicePrefs.specialRequests || '',
          clientNotes: servicePrefs.productConcerns || '',
          approveProducts: servicePrefs.approveProducts,
          notes: `Preferred Day: ${servicePrefs.preferredDay || 'Flexible'}, Time: ${servicePrefs.preferredTime || 'Flexible'}`,
          photoCount: photos.length
        },
        'residential'
      );
      
      // Send confirmation email to client
      try {
        await fetch('/.netlify/functions/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'quote_request_received',
            data: {
              clientEmail: clientInfo.email,
              clientName: clientInfo.firstName,
              propertyAddress: fullAddress,
              includeTerms: true
            }
          })
        });
      } catch (emailError) {
        console.error('Failed to send confirmation email:', emailError);
      }
      
      // Send notification to admin
      try {
        await fetch('/.netlify/functions/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'new_quote_request',
            data: {
              clientName: `${clientInfo.firstName} ${clientInfo.lastName}`,
              clientEmail: clientInfo.email,
              clientPhone: clientInfo.phone,
              propertyAddress: fullAddress,
              condition: HOME_CONDITIONS.find(c => c.value === propertyInfo.condition)?.label,
              frequency: (servicePrefs.frequencies || []).map(f => FREQUENCY_OPTIONS.find(opt => opt.value === f)?.label).join(', '),
              addonsCount: selectedAddons.length,
              photosCount: photos.length
            }
          })
        });
      } catch (emailError) {
        console.error('Failed to send admin notification:', emailError);
      }
      
      setSubmitted(true);
      
    } catch (error) {
      console.error('Failed to submit quote request:', error);
      setErrors({ submit: 'Failed to submit. Please try again or call us at (434) 207-2246.' });
    }
    
    setIsSubmitting(false);
  };
  
  // Success screen
  if (submitted) {
    return (
      <div className="quote-page">
        <div className="quote-container success-page">
          
          {/* Hero Confirmation */}
          <div className="success-hero">
            <div className="success-icon">
              <CheckCircle size={56} />
            </div>
            <h1>You're All Set!</h1>
            <p className="success-subtitle">Your quote request has been submitted. Here's everything you need to know while we prepare your personalized estimate.</p>
          </div>

          {/* Timeline */}
          <div className="whats-next-section">
            <h2 className="section-heading"><Calendar size={22} /> What Happens Next</h2>
            <div className="timeline">
              <div className="timeline-item active">
                <div className="timeline-marker">
                  <CheckCircle size={18} />
                </div>
                <div className="timeline-content">
                  <h4>Request Received</h4>
                  <p>We've got your details and photos — you're in good hands!</p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-marker">
                  <span>2</span>
                </div>
                <div className="timeline-content">
                  <h4>We Review Your Info</h4>
                  <p>Our team verifies your property details, reviews your photos, and builds a custom quote tailored to your home.</p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-marker">
                  <span>3</span>
                </div>
                <div className="timeline-content">
                  <h4>Your Estimate Arrives</h4>
                  <p>Estimates are typically ready the <strong>same business day</strong>. Larger or custom quotes may take a bit longer, but we'll keep you posted.</p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-marker">
                  <span>4</span>
                </div>
                <div className="timeline-content">
                  <h4>Approve & Schedule</h4>
                  <p>Love your quote? Approve it right from the portal and pick your first cleaning date. That's it!</p>
                </div>
              </div>
            </div>
          </div>

          {/* Portal Access Card */}
          <div className="whats-next-section portal-section">
            <div className="portal-access-card">
              <div className="portal-access-left">
                <div className="portal-badge">🔐</div>
                <h2>Your Client Portal is Ready</h2>
                <p>You now have a personal portal where you can manage everything about your service — no phone calls needed.</p>
                <div className="portal-login-instructions">
                  <h4>How to log in:</h4>
                  <div className="login-step">
                    <span className="step-num">1</span>
                    <span>Go to <strong>keelgroup.app/portal</strong></span>
                  </div>
                  <div className="login-step">
                    <span className="step-num">2</span>
                    <span>Enter your email: <strong>{clientInfo.email}</strong></span>
                  </div>
                  <div className="login-step">
                    <span className="step-num">3</span>
                    <span>Your password is the <strong>last 4 digits of your phone number</strong></span>
                  </div>
                </div>
                <a href="/portal" className="portal-cta-btn">
                  Open My Portal <ChevronRight size={18} />
                </a>
              </div>
              <div className="portal-access-right">
                <h4>In your portal you can:</h4>
                <ul className="portal-feature-list">
                  <li><CheckCircle size={16} /> View & approve estimates</li>
                  <li><CheckCircle size={16} /> See upcoming appointments</li>
                  <li><CheckCircle size={16} /> Pay invoices online</li>
                  <li><CheckCircle size={16} /> Message us directly</li>
                  <li><CheckCircle size={16} /> Reschedule or manage services</li>
                  <li><CheckCircle size={16} /> Leave tips for your team</li>
                </ul>
              </div>
            </div>
          </div>

          {/* About Us */}
          <div className="whats-next-section about-section">
            <h2 className="section-heading"><Sparkles size={22} /> A Little About Us</h2>
            <div className="about-grid">
              <div className="about-card">
                <div className="about-icon">🏠</div>
                <h4>Local & Trusted</h4>
                <p>Two Sisters Cleaning & Organizing has been proudly serving Charlottesville and Albemarle County. We're a locally owned, W-2 employer — not a franchise.</p>
              </div>
              <div className="about-card">
                <div className="about-icon">🛡️</div>
                <h4>Insured & Professional</h4>
                <p>We carry full liability insurance and every team member is a vetted, trained employee — not an independent contractor.</p>
              </div>
              <div className="about-card">
                <div className="about-icon">🌿</div>
                <h4>Thoughtful Products</h4>
                <p>We use Simple Green, our in-house alcohol glass cleaner, Odoban floor cleaner, and Beeswax — effective and mindful of your home.</p>
              </div>
              <div className="about-card">
                <div className="about-icon">✨</div>
                <h4>The Tornado Method</h4>
                <p>Our signature cleaning system ensures nothing gets missed. Every room is cleaned top-to-bottom, left-to-right, every time.</p>
              </div>
            </div>
          </div>

          {/* What to Expect on Cleaning Day */}
          <div className="whats-next-section prep-section">
            <h2 className="section-heading"><Home size={22} /> Preparing for Your First Clean</h2>
            <p className="section-intro">Once your estimate is approved and your first appointment is scheduled, here are a few things to know:</p>
            <div className="prep-grid">
              <div className="prep-item">
                <div className="prep-icon">🔑</div>
                <div>
                  <h4>Access</h4>
                  <p>You don't need to be home! Just provide entry instructions (door code, lockbox, hidden key). Keys are signed in/out and stored securely in our locked office.</p>
                </div>
              </div>
              <div className="prep-item">
                <div className="prep-icon">🐾</div>
                <div>
                  <h4>Pets</h4>
                  <p>We love your furry friends! Just let us know about any pets so our team is prepared. We ask that aggressive animals be secured during service.</p>
                </div>
              </div>
              <div className="prep-item">
                <div className="prep-icon">📱</div>
                <div>
                  <h4>Notifications</h4>
                  <p>You'll get text and email updates when your team is on the way, when they arrive, and when they finish. Turn on notifications in your portal settings.</p>
                </div>
              </div>
              <div className="prep-item">
                <div className="prep-icon">💳</div>
                <div>
                  <h4>Payment</h4>
                  <p>Invoices are sent after each cleaning and can be paid online through your portal. We accept all major credit cards.</p>
                </div>
              </div>
              <div className="prep-item">
                <div className="prep-icon">🧹</div>
                <div>
                  <h4>Light Tidying Helps</h4>
                  <p>Picking up clothes, toys, and dishes before your clean helps our team focus on the deep stuff — so you get the most out of every visit.</p>
                </div>
              </div>
              <div className="prep-item">
                <div className="prep-icon">💬</div>
                <div>
                  <h4>Special Requests</h4>
                  <p>Have priorities or problem areas? Send us a message through the portal anytime and we'll make sure your team knows.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact / Questions */}
          <div className="whats-next-section contact-section">
            <div className="contact-card">
              <h2>Questions? We're Here.</h2>
              <p>Don't hesitate to reach out — we'd love to hear from you.</p>
              <div className="contact-methods">
                <a href="tel:+14342072246" className="contact-method">
                  <Phone size={22} />
                  <div>
                    <span className="contact-label">Call or Text</span>
                    <span className="contact-value">(434) 207-2246</span>
                  </div>
                </a>
                <a href="mailto:hello@twosisterscleaningco.com" className="contact-method">
                  <Mail size={22} />
                  <div>
                    <span className="contact-label">Email</span>
                    <span className="contact-value">hello@twosisterscleaningco.com</span>
                  </div>
                </a>
                <a href="/portal" className="contact-method">
                  <Users size={22} />
                  <div>
                    <span className="contact-label">Portal Messages</span>
                    <span className="contact-value">Log in and message us anytime</span>
                  </div>
                </a>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="success-footer">
            <div className="footer-logo">
              <Leaf size={24} />
              <span>Two Sisters Cleaning & Organizing</span>
            </div>
            <p className="footer-tagline-success">From Chaos to Clarity</p>
            <p className="footer-address">Charlottesville & Albemarle County, Virginia</p>
          </div>

        </div>
      </div>
    );
  }
  
  return (
    <div className="quote-page">
      <div className="quote-container">
        {/* Header */}
        <div className="quote-header">
          <div className="quote-logo">
            <Leaf size={36} />
            <div>
              <h1>Two Sisters</h1>
              <span>Cleaning & Organizing</span>
            </div>
          </div>
          <p className="quote-tagline">From Chaos to Clarity</p>
        </div>

        {/* Service Type Selection - Show First */}
        {!serviceType && (
          <div className="form-section service-type-selection">
            <h2>What can we help you with?</h2>
            <p className="section-description">Select the service you're interested in</p>
            
            <div className="service-type-options">
              <button 
                type="button"
                className="service-type-card"
                onClick={() => setServiceType('cleaning')}
              >
                <div className="service-type-icon">🧹</div>
                <h3>Residential Cleaning</h3>
                <p>Recurring cleaning, deep cleans, move in/out</p>
              </button>

              <button 
                type="button"
                className="service-type-card"
                onClick={() => setServiceType('commercial')}
              >
                <div className="service-type-icon">🏢</div>
                <h3>Commercial Cleaning</h3>
                <p>Offices, retail, medical facilities & more</p>
              </button>
              
              <button 
                type="button"
                className="service-type-card"
                onClick={() => setServiceType('hoarding')}
              >
                <div className="service-type-icon">📦</div>
                <h3>Hoarding Help</h3>
                <p>Compassionate decluttering & organizing assistance</p>
              </button>
              
              <button 
                type="button"
                className="service-type-card"
                onClick={() => setServiceType('junk_removal')}
              >
                <div className="service-type-icon">🚛</div>
                <h3>Junk Removal</h3>
                <p>Hauling, donation runs, estate cleanouts</p>
              </button>
              
              <button 
                type="button"
                className="service-type-card"
                onClick={() => setServiceType('custom')}
              >
                <div className="service-type-icon">✨</div>
                <h3>Custom Quote</h3>
                <p>Something else? Let us know what you need</p>
              </button>
            </div>
          </div>
        )}

        {/* Simple Quote Form for Hoarding/Junk/Custom */}
        {(serviceType === 'hoarding' || serviceType === 'junk_removal' || serviceType === 'custom') && !submitted && (
          <div className="form-section simple-quote-form">
            <button 
              type="button" 
              className="back-to-services"
              onClick={() => setServiceType('')}
            >
              ← Back to service selection
            </button>
            
            <h2>
              {serviceType === 'hoarding' && '📦 Hoarding Help Request'}
              {serviceType === 'junk_removal' && '🚛 Junk Removal Request'}
              {serviceType === 'custom' && '✨ Custom Quote Request'}
            </h2>
            <p className="section-description">
              Tell us about your project and we'll get back to you within 24 hours
            </p>
            
            <div className="form-grid">
              <div className="form-field">
                <label>First Name *</label>
                <input
                  type="text"
                  value={clientInfo.firstName}
                  onChange={(e) => setClientInfo({ ...clientInfo, firstName: e.target.value })}
                  placeholder="First name"
                  className={errors.firstName ? 'error' : ''}
                />
              </div>
              
              <div className="form-field">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={clientInfo.lastName}
                  onChange={(e) => setClientInfo({ ...clientInfo, lastName: e.target.value })}
                  placeholder="Last name"
                  className={errors.lastName ? 'error' : ''}
                />
              </div>
            </div>
            
            <div className="form-field">
              <label><Mail size={16} /> Email Address *</label>
              <input
                type="email"
                value={clientInfo.email}
                onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })}
                placeholder="your@email.com"
                className={errors.email ? 'error' : ''}
              />
            </div>
            
            <div className="form-field">
              <label><Phone size={16} /> Phone Number *</label>
              <input
                type="tel"
                value={clientInfo.phone}
                onChange={(e) => setClientInfo({ ...clientInfo, phone: e.target.value })}
                placeholder="(434) 555-1234"
                className={errors.phone ? 'error' : ''}
              />
            </div>
            
            <div className="form-field">
              <label><MapPin size={16} /> Address (optional)</label>
              <input
                type="text"
                value={propertyInfo.street}
                onChange={(e) => setPropertyInfo({ ...propertyInfo, street: e.target.value })}
                placeholder="123 Main Street, Charlottesville, VA"
              />
            </div>
            
            <div className="form-field">
              <label>Tell us about your project *</label>
              <textarea
                value={servicePrefs.customDescription}
                onChange={(e) => setServicePrefs({ ...servicePrefs, customDescription: e.target.value })}
                placeholder={
                  serviceType === 'hoarding' 
                    ? "Describe the situation, number of rooms affected, any time constraints, and how we can best support you..."
                    : serviceType === 'junk_removal'
                    ? "Describe what needs to be removed, approximate amount (truck loads, room size, etc.), and any access considerations..."
                    : "Describe what you need help with and any relevant details..."
                }
                rows={5}
                className={errors.customDescription ? 'error' : ''}
              />
              {errors.customDescription && <span className="error-text">{errors.customDescription}</span>}
            </div>
            
            {/* Photo Upload */}
            <div className="form-field">
              <label><Camera size={16} /> Photos (optional but helpful)</label>
              <p className="field-description">Upload photos to help us understand the scope of work</p>
              
              <div className="photo-upload-area">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                />
                <button 
                  type="button" 
                  className="upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload size={20} />
                  Add Photos
                </button>
                <span className="photo-count">{photos.length}/15 photos</span>
              </div>
              
              {photos.length > 0 && (
                <div className="photo-grid">
                  {photos.map(photo => (
                    <div key={photo.id} className="photo-item">
                      <img src={photo.preview} alt="Upload preview" />
                      <button 
                        type="button"
                        className="remove-photo"
                        onClick={() => setPhotos(photos.filter(p => p.id !== photo.id))}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {photoError && <span className="error-text">{photoError}</span>}
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                className="btn-next btn-submit"
                onClick={handleSimpleQuoteSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <><Loader size={20} className="spin" /> Submitting...</>
                ) : (
                  <>Submit Request <ChevronRight size={20} /></>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Commercial Quote Form */}
        {serviceType === 'commercial' && !submitted && (
          <div className="form-section commercial-quote-form">
            <button 
              type="button" 
              className="back-to-services"
              onClick={() => setServiceType('')}
            >
              ← Back to service selection
            </button>
            
            <h2>🏢 Commercial Cleaning Quote</h2>
            <p className="section-description">
              Tell us about your facility and we'll provide a custom quote
            </p>
            
            {/* Contact Info */}
            <h3 className="subsection-title">Contact Information</h3>
            <div className="form-grid">
              <div className="form-field">
                <label>Your Name *</label>
                <input
                  type="text"
                  value={clientInfo.firstName}
                  onChange={(e) => setClientInfo({ ...clientInfo, firstName: e.target.value })}
                  placeholder="First name"
                  className={errors.firstName ? 'error' : ''}
                />
              </div>
              
              <div className="form-field">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={clientInfo.lastName}
                  onChange={(e) => setClientInfo({ ...clientInfo, lastName: e.target.value })}
                  placeholder="Last name"
                  className={errors.lastName ? 'error' : ''}
                />
              </div>
            </div>
            
            <div className="form-field">
              <label><Mail size={16} /> Email Address *</label>
              <input
                type="email"
                value={clientInfo.email}
                onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })}
                placeholder="your@email.com"
                className={errors.email ? 'error' : ''}
              />
            </div>
            
            <div className="form-field">
              <label><Phone size={16} /> Phone Number *</label>
              <input
                type="tel"
                value={clientInfo.phone}
                onChange={(e) => setClientInfo({ ...clientInfo, phone: e.target.value })}
                placeholder="(434) 555-1234"
                className={errors.phone ? 'error' : ''}
              />
            </div>

            {/* Business Info */}
            <h3 className="subsection-title">Business Information</h3>
            
            <div className="form-field">
              <label>Business Name *</label>
              <input
                type="text"
                value={commercialInfo.businessName}
                onChange={(e) => setCommercialInfo({ ...commercialInfo, businessName: e.target.value })}
                placeholder="Your Company Name"
                className={errors.businessName ? 'error' : ''}
              />
            </div>
            
            <div className="form-field">
              <label><MapPin size={16} /> Business Address *</label>
              <input
                type="text"
                value={propertyInfo.street}
                onChange={(e) => setPropertyInfo({ ...propertyInfo, street: e.target.value })}
                placeholder="123 Main Street, Charlottesville, VA"
                className={errors.street ? 'error' : ''}
              />
            </div>
            
            <div className="form-field">
              <label>Type of Building *</label>
              <select
                value={commercialInfo.buildingType}
                onChange={(e) => setCommercialInfo({ ...commercialInfo, buildingType: e.target.value })}
                className={errors.buildingType ? 'error' : ''}
              >
                <option value="">Select building type...</option>
                <option value="office">Office Building</option>
                <option value="retail">Retail Store</option>
                <option value="medical">Medical/Dental Office</option>
                <option value="restaurant">Restaurant/Food Service</option>
                <option value="gym">Gym/Fitness Center</option>
                <option value="church">Church/Religious Facility</option>
                <option value="school">School/Educational</option>
                <option value="warehouse">Warehouse/Industrial</option>
                <option value="salon">Salon/Spa</option>
                <option value="airbnb">Short-Term Rental/Airbnb</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div className="form-grid">
              <div className="form-field">
                <label>Approximate Square Footage *</label>
                <input
                  type="text"
                  value={commercialInfo.sqft}
                  onChange={(e) => setCommercialInfo({ ...commercialInfo, sqft: e.target.value })}
                  placeholder="e.g., 2500"
                  className={errors.sqft ? 'error' : ''}
                />
              </div>
              
              <div className="form-field">
                <label>Number of Floors</label>
                <select
                  value={commercialInfo.floors}
                  onChange={(e) => setCommercialInfo({ ...commercialInfo, floors: e.target.value })}
                >
                  <option value="1">1 Floor</option>
                  <option value="2">2 Floors</option>
                  <option value="3">3 Floors</option>
                  <option value="4+">4+ Floors</option>
                </select>
              </div>
            </div>
            
            <div className="form-field">
              <label>Desired Cleaning Frequency *</label>
              <select
                value={commercialInfo.frequency}
                onChange={(e) => setCommercialInfo({ ...commercialInfo, frequency: e.target.value })}
              >
                <option value="daily">Daily</option>
                <option value="3x_week">3x Per Week</option>
                <option value="2x_week">2x Per Week</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Every 2 Weeks</option>
                <option value="monthly">Monthly</option>
                <option value="one_time">One-Time Deep Clean</option>
              </select>
            </div>
            
            <div className="form-field">
              <label>Preferred Schedule</label>
              <input
                type="text"
                value={commercialInfo.preferredSchedule}
                onChange={(e) => setCommercialInfo({ ...commercialInfo, preferredSchedule: e.target.value })}
                placeholder="e.g., After 6pm weekdays, Weekend mornings"
              />
            </div>
            
            <div className="form-field">
              <label>Additional Details</label>
              <textarea
                value={commercialInfo.additionalDetails}
                onChange={(e) => setCommercialInfo({ ...commercialInfo, additionalDetails: e.target.value })}
                placeholder="Tell us about any specific needs: restrooms, break room, high-traffic areas, special equipment, security requirements, etc."
                rows={4}
              />
            </div>
            
            {/* Photo Upload */}
            <div className="form-field">
              <label><Camera size={16} /> Photos (optional but helpful)</label>
              <p className="field-description">Upload photos of your facility to help us provide an accurate quote</p>
              
              <div className="photo-upload-area">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                />
                <button 
                  type="button" 
                  className="upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload size={20} />
                  Add Photos
                </button>
                <span className="photo-count">{photos.length}/15 photos</span>
              </div>
              
              {photos.length > 0 && (
                <div className="photo-grid">
                  {photos.map(photo => (
                    <div key={photo.id} className="photo-item">
                      <img src={photo.preview} alt="Upload preview" />
                      <button 
                        type="button"
                        className="remove-photo"
                        onClick={() => setPhotos(photos.filter(p => p.id !== photo.id))}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {photoError && <span className="error-text">{photoError}</span>}
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                className="btn-next btn-submit"
                onClick={handleCommercialQuoteSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <><Loader size={20} className="spin" /> Submitting...</>
                ) : (
                  <>Request Quote <ChevronRight size={20} /></>
                )}
              </button>
            </div>
          </div>
        )}
        
        {/* Progress Bar - Only show for cleaning quotes */}
        {serviceType === 'cleaning' && (
          <>
            <button 
              type="button" 
              className="back-to-services"
              onClick={() => setServiceType('')}
            >
              ← Back to service selection
            </button>
            
            <div className="progress-bar">
          <div className="progress-steps">
            <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
              <span className="step-number">1</span>
              <span className="step-label">Your Info</span>
            </div>
            <div className={`progress-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
              <span className="step-number">2</span>
              <span className="step-label">Property</span>
            </div>
            <div className={`progress-step ${step >= 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''}`}>
              <span className="step-number">3</span>
              <span className="step-label">Services</span>
            </div>
            <div className={`progress-step ${step >= 4 ? 'active' : ''} ${step > 4 ? 'completed' : ''}`}>
              <span className="step-number">4</span>
              <span className="step-label">Photos</span>
            </div>
            <div className={`progress-step ${step >= 5 ? 'active' : ''}`}>
              <span className="step-number">5</span>
              <span className="step-label">Terms</span>
            </div>
          </div>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${((step - 1) / 4) * 100}%` }}></div>
          </div>
        </div>
        
        {/* Step 1: Client Info */}
        {step === 1 && (
          <div className="form-section">
            <h2><User size={24} /> Your Information</h2>
            <p className="section-description">Let's start with your contact details</p>
            
            <div className="form-grid">
              <div className="form-field">
                <label>First Name *</label>
                <input
                  type="text"
                  value={clientInfo.firstName}
                  onChange={(e) => setClientInfo({ ...clientInfo, firstName: e.target.value })}
                  placeholder="First name"
                  className={errors.firstName ? 'error' : ''}
                />
                {errors.firstName && <span className="error-text">{errors.firstName}</span>}
              </div>
              
              <div className="form-field">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={clientInfo.lastName}
                  onChange={(e) => setClientInfo({ ...clientInfo, lastName: e.target.value })}
                  placeholder="Last name"
                  className={errors.lastName ? 'error' : ''}
                />
                {errors.lastName && <span className="error-text">{errors.lastName}</span>}
              </div>
            </div>
            
            <div className="form-field">
              <label><Mail size={16} /> Email Address *</label>
              <input
                type="email"
                value={clientInfo.email}
                onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })}
                placeholder="your@email.com"
                className={errors.email ? 'error' : ''}
              />
              {errors.email && <span className="error-text">{errors.email}</span>}
            </div>
            
            <div className="form-field">
              <label><Phone size={16} /> Phone Number *</label>
              <input
                type="tel"
                value={clientInfo.phone}
                onChange={(e) => setClientInfo({ ...clientInfo, phone: e.target.value })}
                placeholder="(434) 555-1234"
                className={errors.phone ? 'error' : ''}
              />
              {errors.phone && <span className="error-text">{errors.phone}</span>}
            </div>
            
            <div className={`checkbox-field ${errors.marketingOptIn ? 'error' : ''}`}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={clientInfo.marketingOptIn}
                  onChange={(e) => setClientInfo({ ...clientInfo, marketingOptIn: e.target.checked })}
                />
                <span className="checkmark"></span>
                <span>I agree to receive emails from Two Sisters Cleaning, including promotions and updates. *</span>
              </label>
              {errors.marketingOptIn && <span className="error-text">{errors.marketingOptIn}</span>}
            </div>
            
            <div className="form-actions">
              <button type="button" className="btn-next" onClick={nextStep}>
                Continue <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
        
        {/* Step 2: Property Info */}
        {step === 2 && (
          <div className="form-section">
            <h2><Home size={24} /> Property Details</h2>
            <p className="section-description">Tell us about your home</p>
            
            <div className="form-field">
              <label><MapPin size={16} /> Street Address *</label>
              <input
                type="text"
                value={propertyInfo.street}
                onChange={(e) => setPropertyInfo({ ...propertyInfo, street: e.target.value })}
                placeholder="123 Main Street"
                className={errors.street ? 'error' : ''}
              />
              {errors.street && <span className="error-text">{errors.street}</span>}
            </div>
            
            <div className="form-grid form-grid-3">
              <div className="form-field">
                <label>City *</label>
                <input
                  type="text"
                  value={propertyInfo.city}
                  onChange={(e) => setPropertyInfo({ ...propertyInfo, city: e.target.value })}
                  placeholder="Charlottesville"
                  className={errors.city ? 'error' : ''}
                />
                {errors.city && <span className="error-text">{errors.city}</span>}
              </div>
              
              <div className="form-field">
                <label>State</label>
                <select
                  value={propertyInfo.state}
                  onChange={(e) => setPropertyInfo({ ...propertyInfo, state: e.target.value })}
                >
                  <option value="VA">Virginia</option>
                </select>
              </div>
              
              <div className="form-field">
                <label>ZIP Code *</label>
                <input
                  type="text"
                  value={propertyInfo.zip}
                  onChange={(e) => setPropertyInfo({ ...propertyInfo, zip: e.target.value })}
                  placeholder="22901"
                  className={errors.zip ? 'error' : ''}
                />
                {errors.zip && <span className="error-text">{errors.zip}</span>}
              </div>
            </div>
            
            <div className="number-inputs-row">
              <NumberInput
                label="Bedrooms"
                value={propertyInfo.bedrooms}
                onChange={(v) => setPropertyInfo({ ...propertyInfo, bedrooms: v })}
                min={1}
                max={10}
              />
              <NumberInput
                label="Full Baths"
                value={propertyInfo.fullBaths}
                onChange={(v) => setPropertyInfo({ ...propertyInfo, fullBaths: v })}
                min={1}
                max={10}
              />
              <NumberInput
                label="Half Baths"
                value={propertyInfo.halfBaths}
                onChange={(v) => setPropertyInfo({ ...propertyInfo, halfBaths: v })}
                min={0}
                max={5}
              />
            </div>
            
            <div className="form-field">
              <label>How would you describe your home's current condition? *</label>
              <div className={`condition-options ${errors.condition ? 'error' : ''}`}>
                {HOME_CONDITIONS.map(condition => (
                  <div
                    key={condition.value}
                    className={`condition-card ${propertyInfo.condition === condition.value ? 'selected' : ''}`}
                    onClick={() => setPropertyInfo({ ...propertyInfo, condition: condition.value })}
                  >
                    <span className="condition-emoji">{condition.emoji}</span>
                    <span className="condition-label">{condition.label}</span>
                    <span className="condition-desc">{condition.description}</span>
                  </div>
                ))}
              </div>
              {errors.condition && <span className="error-text">{errors.condition}</span>}
            </div>
            
            <div className="number-inputs-row">
              <NumberInput
                label="People in Home"
                value={propertyInfo.people}
                onChange={(v) => setPropertyInfo({ ...propertyInfo, people: v })}
                min={1}
                max={15}
              />
              <NumberInput
                label="Shedding Pets"
                value={propertyInfo.sheddingPets}
                onChange={(v) => setPropertyInfo({ ...propertyInfo, sheddingPets: v })}
                min={0}
                max={10}
              />
              <NumberInput
                label="Glass Shower Doors"
                value={propertyInfo.glassShowerDoors}
                onChange={(v) => setPropertyInfo({ ...propertyInfo, glassShowerDoors: v })}
                min={0}
                max={10}
              />
            </div>
            
            {propertyInfo.sheddingPets > 0 && (
              <div className="form-field">
                <label><Dog size={16} /> Pet Details (optional)</label>
                <input
                  type="text"
                  value={propertyInfo.petDetails}
                  onChange={(e) => setPropertyInfo({ ...propertyInfo, petDetails: e.target.value })}
                  placeholder="e.g., 2 dogs - Golden Retriever and Lab"
                />
              </div>
            )}
            
            <div className="form-actions">
              <button type="button" className="btn-back" onClick={prevStep}>
                Back
              </button>
              <button type="button" className="btn-next" onClick={nextStep}>
                Continue <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
        
        {/* Step 3: Service Preferences & Add-ons */}
        {step === 3 && (
          <div className="form-section">
            <h2><Sparkles size={24} /> Service Preferences</h2>
            
            {/* Service Type Notice */}
            <div className="info-box">
              <Info size={20} />
              <div>
                <strong>Your first cleaning will be an Initial Deep Clean</strong>
                <p>This allows us to get your home to our standard before beginning regular service.</p>
              </div>
            </div>
            
            {/* Frequency Selection - Multiple allowed */}
            <div className="form-field">
              <label>Which frequency options would you like quotes for? *</label>
              <p className="field-hint">Select all that interest you - we'll provide pricing for each</p>
              <div className={`frequency-options multi-select ${errors.frequencies ? 'error' : ''}`}>
                {FREQUENCY_OPTIONS.map(freq => {
                  const isSelected = servicePrefs.frequencies?.includes(freq.value);
                  return (
                    <div
                      key={freq.value}
                      className={`frequency-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => {
                        const current = servicePrefs.frequencies || [];
                        if (isSelected) {
                          // Don't allow removing if it's the only one
                          if (current.length > 1) {
                            setServicePrefs({ ...servicePrefs, frequencies: current.filter(f => f !== freq.value) });
                          }
                        } else {
                          setServicePrefs({ ...servicePrefs, frequencies: [...current, freq.value] });
                        }
                      }}
                    >
                      <div className="frequency-checkbox">
                        {isSelected && <CheckCircle size={18} />}
                      </div>
                      <span className="frequency-label">{freq.label}</span>
                      <span className="frequency-desc">{freq.description}</span>
                    </div>
                  );
                })}
              </div>
              {errors.frequencies && <span className="error-text">{errors.frequencies}</span>}
            </div>
            
            <div className="form-grid">
              <div className="form-field">
                <label>Preferred Day (optional)</label>
                <select
                  value={servicePrefs.preferredDay}
                  onChange={(e) => setServicePrefs({ ...servicePrefs, preferredDay: e.target.value })}
                >
                  <option value="">No preference</option>
                  <option value="monday">Monday</option>
                  <option value="tuesday">Tuesday</option>
                  <option value="wednesday">Wednesday</option>
                  <option value="thursday">Thursday</option>
                  <option value="friday">Friday</option>
                </select>
              </div>
              
              <div className="form-field">
                <label>Preferred Time (optional)</label>
                <select
                  value={servicePrefs.preferredTime}
                  onChange={(e) => setServicePrefs({ ...servicePrefs, preferredTime: e.target.value })}
                >
                  <option value="">No preference</option>
                  <option value="morning">Morning (8am-12pm)</option>
                  <option value="afternoon">Afternoon (12pm-5pm)</option>
                </select>
              </div>
            </div>
            
            {/* Cleaning Products */}
            <div className="products-section">
              <h3>Our Cleaning Products</h3>
              <p>We use the following eco-friendly and effective products:</p>
              <div className="products-list">
                {CLEANING_PRODUCTS.map((product, idx) => (
                  <div key={idx} className="product-item">
                    <CheckCircle size={16} />
                    <span><strong>{product.name}</strong> - {product.description}</span>
                  </div>
                ))}
              </div>
              
              <div className={`form-field ${errors.approveProducts ? 'error' : ''}`}>
                <label>Do you approve of these cleaning products? *</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="approveProducts"
                      checked={servicePrefs.approveProducts === true}
                      onChange={() => setServicePrefs({ ...servicePrefs, approveProducts: true })}
                    />
                    <span>Yes, these products are fine</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="approveProducts"
                      checked={servicePrefs.approveProducts === false}
                      onChange={() => setServicePrefs({ ...servicePrefs, approveProducts: false })}
                    />
                    <span>I have concerns or allergies</span>
                  </label>
                </div>
                {errors.approveProducts && <span className="error-text">{errors.approveProducts}</span>}
              </div>
              
              {servicePrefs.approveProducts === false && (
                <div className="form-field">
                  <label>Please describe your concerns:</label>
                  <textarea
                    value={servicePrefs.productConcerns}
                    onChange={(e) => setServicePrefs({ ...servicePrefs, productConcerns: e.target.value })}
                    placeholder="Any allergies, sensitivities, or product preferences..."
                    rows={3}
                  />
                </div>
              )}
            </div>
            
            {/* Add-ons */}
            <div className="addons-section">
              <h3>Additional Services (Optional)</h3>
              <p>Select any add-on services you'd like included in your quote</p>
              
              {/* Oven Cleaning */}
              <div className="addon-group">
                <div 
                  className={`addon-header ${expandedSections.oven ? 'expanded' : ''}`}
                  onClick={() => {
                    const willExpand = !expandedSections.oven;
                    setExpandedSections(prev => ({ ...prev, oven: willExpand }));
                    if (willExpand && !addons.ovenClean) {
                      setAddons(prev => ({ ...prev, ovenClean: true }));
                    }
                  }}
                >
                  <div className="addon-header-left">
                    <input
                      type="checkbox"
                      checked={addons.ovenClean}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setAddons(prev => ({ ...prev, ovenClean: checked }));
                        if (checked) {
                          setExpandedSections(prev => ({ ...prev, oven: true }));
                        } else {
                          setExpandedSections(prev => ({ ...prev, oven: false }));
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span>Inside Oven Cleaning</span>
                  </div>
                  <ChevronDown size={20} className={expandedSections.oven ? 'rotated' : ''} />
                </div>
                
                {expandedSections.oven && addons.ovenClean && (
                  <div className="addon-content">
                    <p className="addon-instruction">Select the condition that best matches your oven:</p>
                    
                    <div className="condition-select-cards">
                      {OVEN_CONDITIONS.map(cond => (
                        <div
                          key={cond.value}
                          className={`condition-select-card ${addons.ovenCondition === cond.value ? 'selected' : ''}`}
                          onClick={() => setAddons({ ...addons, ovenCondition: cond.value })}
                        >
                          <div className="csc-image">
                            <img src={cond.image} alt={cond.label} className="condition-photo" />
                          </div>
                          <div className="csc-info">
                            <span className="csc-label">{cond.label}</span>
                            <span className="csc-desc">{cond.description}</span>
                            <span className="csc-price">{cond.price}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="checkbox-field blue-ceramic-check">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={addons.ovenIsBlueCeramic}
                          onChange={(e) => setAddons({ ...addons, ovenIsBlueCeramic: e.target.checked })}
                        />
                        <span className="checkmark"></span>
                        <span>This is a blue ceramic oven</span>
                      </label>
                      <div className="info-tooltip">
                        <Info size={14} />
                        <span>Blue ceramic ovens require special cleaning products to avoid damage. Please let us know so we come prepared!</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Fridge Cleaning */}
              <div className="addon-group">
                <div 
                  className={`addon-header ${expandedSections.fridge ? 'expanded' : ''}`}
                  onClick={() => {
                    const willExpand = !expandedSections.fridge;
                    setExpandedSections(prev => ({ ...prev, fridge: willExpand }));
                    if (willExpand && !addons.fridgeClean) {
                      setAddons(prev => ({ ...prev, fridgeClean: true }));
                    }
                  }}
                >
                  <div className="addon-header-left">
                    <input
                      type="checkbox"
                      checked={addons.fridgeClean}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setAddons(prev => ({ ...prev, fridgeClean: checked }));
                        if (checked) {
                          setExpandedSections(prev => ({ ...prev, fridge: true }));
                        } else {
                          setExpandedSections(prev => ({ ...prev, fridge: false }));
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span>Inside Fridge Cleaning</span>
                  </div>
                  <ChevronDown size={20} className={expandedSections.fridge ? 'rotated' : ''} />
                </div>
                
                {expandedSections.fridge && addons.fridgeClean && (
                  <div className="addon-content">
                    <p className="addon-instruction">Select the condition that best matches your fridge:</p>
                    
                    <div className="condition-select-cards">
                      {FRIDGE_CONDITIONS.map(cond => (
                        <div
                          key={cond.value}
                          className={`condition-select-card ${addons.fridgeCondition === cond.value ? 'selected' : ''}`}
                          onClick={() => setAddons({ ...addons, fridgeCondition: cond.value })}
                        >
                          <div className="csc-image">
                            <img src={cond.image} alt={cond.label} className="condition-photo" />
                          </div>
                          <div className="csc-info">
                            <span className="csc-label">{cond.label}</span>
                            <span className="csc-desc">{cond.description}</span>
                            <span className="csc-price">{cond.price}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Window Services */}
              <div className="addon-group">
                <div 
                  className={`addon-header ${expandedSections.windows ? 'expanded' : ''}`}
                  onClick={() => toggleSection('windows')}
                >
                  <div className="addon-header-left">
                    <span>Window Services</span>
                  </div>
                  <ChevronDown size={20} className={expandedSections.windows ? 'rotated' : ''} />
                </div>
                
                {expandedSections.windows && (
                  <div className="addon-content">
                    <div className="number-inputs-row">
                      <NumberInput
                        label="Interior Windows ($5/ea)"
                        value={addons.interiorWindows}
                        onChange={(v) => setAddons({ ...addons, interiorWindows: v })}
                        min={0}
                        max={50}
                      />
                      <NumberInput
                        label="Window Tracks ($10/ea)"
                        value={addons.windowTracks}
                        onChange={(v) => setAddons({ ...addons, windowTracks: v })}
                        min={0}
                        max={50}
                      />
                      <NumberInput
                        label="Blinds ($10/ea)"
                        value={addons.blinds}
                        onChange={(v) => setAddons({ ...addons, blinds: v })}
                        min={0}
                        max={50}
                      />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Other Services */}
              <div className="addon-group">
                <div 
                  className={`addon-header ${expandedSections.other ? 'expanded' : ''}`}
                  onClick={() => toggleSection('other')}
                >
                  <div className="addon-header-left">
                    <span>Other Services</span>
                  </div>
                  <ChevronDown size={20} className={expandedSections.other ? 'rotated' : ''} />
                </div>
                
                {expandedSections.other && (
                  <div className="addon-content">
                    <div className="checkbox-field">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={addons.organizing}
                          onChange={(e) => setAddons({ ...addons, organizing: e.target.checked })}
                        />
                        <span className="checkmark"></span>
                        <span>Organizing Services ($65/hour)</span>
                      </label>
                    </div>
                    
                    {addons.organizing && (
                      <NumberInput
                        label="Estimated Hours"
                        value={addons.organizingHours}
                        onChange={(v) => setAddons({ ...addons, organizingHours: v })}
                        min={1}
                        max={10}
                      />
                    )}
                    
                    <div className="checkbox-field">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={addons.groutCleaning}
                          onChange={(e) => setAddons({ ...addons, groutCleaning: e.target.checked })}
                        />
                        <span className="checkmark"></span>
                        <span>Grout Cleaning (Custom Quote - please include photos)</span>
                      </label>
                    </div>
                    
                    <div className="checkbox-field">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={addons.extraBuildup}
                          onChange={(e) => setAddons({ ...addons, extraBuildup: e.target.checked })}
                        />
                        <span className="checkmark"></span>
                        <span>Extra Build-Up Areas (Custom Quote - please include photos)</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Special Requests */}
            <div className="form-field">
              <label>Special Requests or Notes (optional)</label>
              <textarea
                value={servicePrefs.specialRequests}
                onChange={(e) => setServicePrefs({ ...servicePrefs, specialRequests: e.target.value })}
                placeholder="Any specific areas of concern, access instructions, or other notes..."
                rows={4}
              />
            </div>
            
            <div className="form-actions">
              <button type="button" className="btn-back" onClick={prevStep}>
                Back
              </button>
              <button type="button" className="btn-next" onClick={nextStep}>
                Continue <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
        
        {/* Step 4: Photos */}
        {step === 4 && (
          <div className="form-section">
            <h2><Camera size={24} /> Photos (Optional)</h2>
            <p className="section-description">
              Photos help us provide a more accurate quote. Please include:
            </p>
            <ul className="photo-guidelines">
              <li>Kitchen (counters, stovetop, sink area)</li>
              <li>Bathrooms (shower, tub, toilet area)</li>
              <li>Any problem areas or areas of concern</li>
            </ul>
            
            <div className="photo-upload-area">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoUpload}
                accept="image/*"
                multiple
                style={{ display: 'none' }}
              />
              
              <button 
                type="button" 
                className="upload-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={photos.length >= 15}
              >
                <Upload size={24} />
                <span>Upload Photos</span>
                <span className="upload-hint">{photos.length}/15 photos</span>
              </button>
              
              {photoError && <div className="photo-error">{photoError}</div>}
            </div>
            
            {photos.length > 0 && (
              <div className="photos-preview">
                {photos.map(photo => (
                  <div key={photo.id} className="photo-item">
                    <img src={photo.preview} alt="Upload preview" />
                    <button 
                      type="button" 
                      className="remove-photo"
                      onClick={() => removePhoto(photo.id)}
                    >
                      <X size={16} />
                    </button>
                    <input
                      type="text"
                      placeholder="Label (e.g., Kitchen sink)"
                      value={photo.label}
                      onChange={(e) => updatePhotoLabel(photo.id, e.target.value)}
                      className="photo-label-input"
                    />
                  </div>
                ))}
              </div>
            )}
            
            <div className="form-actions">
              <button type="button" className="btn-back" onClick={prevStep}>
                Back
              </button>
              <button type="button" className="btn-next" onClick={nextStep}>
                Continue <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
        
        {/* Step 5: Terms & Conditions */}
        {step === 5 && (
          <div className="form-section terms-section">
            <h2>📋 Terms & Conditions</h2>
            <p className="section-description">
              Please read and agree to our terms before submitting your request.
            </p>
            
            <div className="terms-content">
              <div className="terms-intro">
                <strong>Please read the following terms carefully.</strong> By booking a service with Two Sisters Cleaning & Organizing LLC, you agree to be bound by these terms and conditions. You must be at least 18 years of age to enter into this agreement.
              </div>
              
              <div className="terms-scroll">
                <div className="term-section">
                  <h4>Service Minimums & Deposits</h4>
                  <p>All cleaning services have a minimum charge of <strong>$125</strong>. New residential clients are required to pay <strong>in full</strong> before their appointment date to secure their appointment.</p>
                </div>
                
                <div className="term-section">
                  <h4>Access & Entry</h4>
                  <p>Clients are not required to be home during the cleaning appointment; however, you must provide clear access instructions (door code, lockbox combination, key location, etc.) prior to your scheduled service. If a key is provided, it will be signed in and out by our team and securely stored in a locked office within our commercial building, accessible only by management.</p>
                </div>
                
                <div className="term-section">
                  <h4>Cancellation & Rescheduling Policy</h4>
                  <p>Each client is allowed <strong>one grace cancellation</strong> with no fee when cancelled with less than 48 hours' notice due to illness or emergency. After your grace cancellation has been used, cancellations or reschedules made within 48 hours of your scheduled appointment will incur a <strong>50% cancellation fee</strong> based on your quoted service price.</p>
                </div>
                
                <div className="term-section">
                  <h4>Payment Terms</h4>
                  <p>We accept <strong>card payments only</strong>. Cash may be left as a tip for your cleaning team but cannot be applied toward service charges. Payment is due upon completion of service unless otherwise arranged.</p>
                  <div className="fee-table">
                    <div className="fee-row header">
                      <span>Status</span>
                      <span>Fee</span>
                    </div>
                    <div className="fee-row">
                      <span>Past due (after 24-hour grace period)</span>
                      <span>Flat $25 late fee</span>
                    </div>
                    <div className="fee-row">
                      <span>Each additional day past due</span>
                      <span>10% added per day</span>
                    </div>
                    <div className="fee-row">
                      <span>Three or more late payments</span>
                      <span>Services discontinued</span>
                    </div>
                  </div>
                </div>
                
                <div className="term-section">
                  <h4>Recurring Service Agreements</h4>
                  <p>Recurring cleaning schedules (weekly, bi-weekly, monthly) may be cancelled at any time with <strong>two weeks' notice</strong>. Pricing for recurring services may be adjusted at any time; clients will be notified of any rate changes prior to their next scheduled service.</p>
                </div>
                
                <div className="term-section">
                  <h4>Home Conditions & Special Services</h4>
                  <p>Water and electricity must be operational at the time of service. The home's HVAC system must be set to a reasonable temperature between <strong>64°F and 75°F</strong> for the safety and comfort of our cleaning team.</p>
                  <p>If the home is found to be in significantly worse condition than described, we reserve the right to adjust pricing accordingly or reschedule as a deep clean at an additional charge.</p>
                </div>
                
                <div className="term-section">
                  <h4>Service Limitations</h4>
                  <p>All surfaces to be cleaned must be reachable with a <strong>6-foot duster or less</strong>. Our team members may only use step ladders with a maximum of <strong>three steps</strong>. We do not move heavy furniture.</p>
                  <p>We <strong>do not</strong> clean up animal urine, feces, needles, or hazardous materials without special consideration and additional fees.</p>
                </div>
                
                <div className="term-section">
                  <h4>Pet Policy</h4>
                  <p>All pets must be <strong>secured</strong> during the cleaning appointment for the safety of both your pets and our team members.</p>
                </div>
                
                <div className="term-section">
                  <h4>Firearm Safety</h4>
                  <p>All firearms in the home must be safely stored and secured prior to our team's arrival.</p>
                </div>
                
                <div className="term-section">
                  <h4>Security Cameras & Recording Devices</h4>
                  <p>Clients must disclose the presence of any security cameras or audio/video recording devices in the home at the time of booking.</p>
                </div>
                
                <div className="term-section">
                  <h4>Parking</h4>
                  <p>Adequate parking must be available at <strong>no cost</strong> to our staff. If parking fees are unavoidable, the client will be charged for any parking costs incurred.</p>
                </div>
                
                <div className="term-section">
                  <h4>Client Conduct</h4>
                  <p>We ask that clients allow our team to work independently without interference. Harassment or excessive interruption of our cleaning staff will not be tolerated and may result in immediate termination of service.</p>
                </div>
                
                <div className="term-section">
                  <h4>Damage & Liability</h4>
                  <p>Two Sisters Cleaning & Organizing LLC carries liability insurance coverage. We are <strong>not responsible</strong> for items that are precariously placed, unstable, or improperly secured. Clients are responsible for removing or securing fragile, valuable, or sentimental items prior to service.</p>
                </div>
                
                <div className="term-section highlight-section">
                  <h4>⚠️ Waiver of Liability for Post-Service Injuries</h4>
                  <p>Two Sisters Cleaning & Organizing LLC is not liable for any injuries, slips, falls, or accidents that occur after our team has left the premises. Clients and household members are advised to exercise caution on freshly cleaned floors until fully dry. Our cleaning products do not contain oils or leave residual slick surfaces.</p>
                  <p>To the fullest extent permitted by law, the client waives any and all claims against Two Sisters Cleaning & Organizing LLC, its owners, employees, and agents for personal injury, property damage, or any other loss occurring after the completion of services and departure of our cleaning team from the premises.</p>
                </div>
                
                <div className="term-section highlight-section">
                  <h4>⚠️ Indemnification</h4>
                  <p>The client agrees to indemnify, defend, and hold harmless Two Sisters Cleaning & Organizing LLC, its owners, officers, employees, and agents from and against any and all claims, damages, losses, liabilities, costs, and expenses (including reasonable attorney's fees) arising out of or related to: (a) any third-party claims for injury or damage occurring on the client's premises after service completion; (b) the client's breach of any term of this agreement; (c) any misrepresentation or negligence by the client; or (d) any claims arising from conditions at the premises that were not disclosed to Two Sisters Cleaning & Organizing LLC prior to service.</p>
                </div>
                
                <div className="term-section">
                  <h4>Satisfaction Guarantee</h4>
                  <p>If you are unhappy with any aspect of your cleaning, please notify us <strong>within 24 hours</strong> of service completion. We will send a team member back within 48 hours to address the issue at no additional charge. <strong>Photos are required</strong> to document the concern.</p>
                  <p>If the client or any third party attempts to remedy, re-clean, or alter the area of concern before our team has the opportunity to inspect and address the issue, the satisfaction guarantee is void and full payment remains due.</p>
                </div>
                
                <div className="term-section">
                  <h4>Photography & Marketing</h4>
                  <p>Our team may take photographs during or after service for quality assurance and marketing purposes. We will <strong>never</strong> include client-identifying information in any published content.</p>
                </div>
                
                <div className="term-section">
                  <h4>Right to Refuse or Discontinue Service</h4>
                  <p>Two Sisters Cleaning & Organizing LLC reserves the right to refuse or discontinue service to any client at any time, for any reason.</p>
                </div>
                
                <div className="term-section">
                  <h4>Governing Law & Dispute Resolution</h4>
                  <p>This agreement shall be governed by the laws of the <strong>Commonwealth of Virginia</strong>. Any disputes shall be resolved through binding arbitration in <strong>Albemarle County, Virginia</strong>.</p>
                </div>
              </div>
            </div>
            
            <div className="terms-agreement">
              <h3>Agreement & Acknowledgment</h3>
              
              <div className={`terms-checkbox ${errors.termsAgree ? 'error' : ''}`}>
                <label>
                  <input
                    type="checkbox"
                    checked={termsAcceptance.termsAgree}
                    onChange={(e) => setTermsAcceptance({ ...termsAcceptance, termsAgree: e.target.checked })}
                  />
                  <span className="checkmark"></span>
                  <span>I have read, understand, and agree to all terms and conditions outlined above. *</span>
                </label>
              </div>
              
              <div className={`terms-checkbox ${errors.ageConfirm ? 'error' : ''}`}>
                <label>
                  <input
                    type="checkbox"
                    checked={termsAcceptance.ageConfirm}
                    onChange={(e) => setTermsAcceptance({ ...termsAcceptance, ageConfirm: e.target.checked })}
                  />
                  <span className="checkmark"></span>
                  <span>I confirm that I am at least 18 years of age and have the legal authority to enter into this agreement. *</span>
                </label>
              </div>
              
              <div className={`terms-checkbox ${errors.paymentAgree ? 'error' : ''}`}>
                <label>
                  <input
                    type="checkbox"
                    checked={termsAcceptance.paymentAgree}
                    onChange={(e) => setTermsAcceptance({ ...termsAcceptance, paymentAgree: e.target.checked })}
                  />
                  <span className="checkmark"></span>
                  <span>I understand and agree to the payment terms, including the deposit requirement, late fees, and cancellation policy. *</span>
                </label>
              </div>
              
              <div className={`terms-checkbox ${errors.photoAgree ? 'error' : ''}`}>
                <label>
                  <input
                    type="checkbox"
                    checked={termsAcceptance.photoAgree}
                    onChange={(e) => setTermsAcceptance({ ...termsAcceptance, photoAgree: e.target.checked })}
                  />
                  <span className="checkmark"></span>
                  <span>I consent to photography for quality assurance and marketing purposes as described above. *</span>
                </label>
              </div>
              
              <div className={`terms-checkbox ${errors.arbitrationAgree ? 'error' : ''}`}>
                <label>
                  <input
                    type="checkbox"
                    checked={termsAcceptance.arbitrationAgree}
                    onChange={(e) => setTermsAcceptance({ ...termsAcceptance, arbitrationAgree: e.target.checked })}
                  />
                  <span className="checkmark"></span>
                  <span>I agree to resolve any disputes through binding arbitration in Albemarle County, Virginia, and waive my right to a jury trial. *</span>
                </label>
              </div>
              
              <div className={`terms-checkbox ${errors.liabilityAgree ? 'error' : ''}`}>
                <label>
                  <input
                    type="checkbox"
                    checked={termsAcceptance.liabilityAgree}
                    onChange={(e) => setTermsAcceptance({ ...termsAcceptance, liabilityAgree: e.target.checked })}
                  />
                  <span className="checkmark"></span>
                  <span>I acknowledge and accept the waiver of liability for post-service injuries and agree to the indemnification terms outlined above. *</span>
                </label>
              </div>
            </div>
            
            {errors.submit && (
              <div className="submit-error">
                <AlertCircle size={20} />
                {errors.submit}
              </div>
            )}
            
            <div className="form-actions">
              <button type="button" className="btn-back" onClick={prevStep}>
                Back
              </button>
              <button 
                type="button" 
                className="btn-submit"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader size={20} className="spinning" />
                    Submitting...
                  </>
                ) : (
                  <>
                    I Agree - Submit Request <CheckCircle size={20} />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
        </>
        )}
        
        {/* Footer */}
        <div className="quote-footer">
          <p>Questions? Call us at <a href="tel:+14342072246">(434) 207-2246</a></p>
          <p className="footer-tagline">Two Sisters Cleaning & Organizing · Charlottesville, VA</p>
        </div>
      </div>
    </div>
  );
}

export default QuoteRequest;
